import './product-compare.scss';
import 'intersection-observer';
import ParameterValidator from '@mvps-genesis/parameter-validator';
import MicroModal from 'micromodal';
import Stickyfill from 'stickyfill';

/*
 *--------------------------------------------------------------------------
 * Product Compare
 *--------------------------------------------------------------------------
 *
 * Compare a set of products.
 *
 */
export default class ProductCompare {

	/**
	 * API settings object
	 *
	 * @var	Object
	 * @property	String	function	The product comparison API function
	 * @property	String	url			The product comparison API URL
	 */
	#api;

	/**
	 * Compare settings object
	 *
	 * @var	Object
	 * @property	String		elementsSelector		The product comparison elements selector
	 * @property	Element[]	elements				The product comparison elements
	 * @property	Integer		limit					The product comparison list limit
	 * @property	Array		list					The product comparison list
	 * @property	Object		product					The product comparison product card settings object
	 * @property	Object		product.idAttribute		The product comparison product card product id data attribute
	 * @property	Object		product.imageAttribute	The product comparison product card product image path data attribute
	 * @property	Object		product.nameAttribute	The product comparison product card product name data attribute
	 */
	#compare;

	/**
	 * Product compare cookie name used for persisting the comparison list
	 *
	 * @var String
	 */
	#cookieName;

	/**
	 * Media query list
	 *
	 * @var	MediaQueryList
	 */
	#mql;

	/**
	 * Class options object. See class constructor() for option definitions.
	 *
	 * @var Object
	 */
	#options;

	/**
	 * Preview settings object
	 *
	 * @var	Object
	 * @property	String	activeClass						The preview section "is active" class name
	 * @property	Element	clearAttribute					The preview section clear all attribute
	 * @property	Element	container						The preview section container element
	 * @property	Element	itemsSelector					The preview section items selector
	 * @property	Element	itemsContainer					The preview section items container element
	 * @property	Element	previewItems					The preview section item elements
	 * @property	Object	product							The preview section product object
	 * @property	String	product.productsContainerClass	The preview section products container
	 * @property	String	product.containerClass			The preview section product card container
	 * @property	String	product.removeClass				The preview section product card remove action class name
	 * @property	String	product.removeIconClass			The preview section product card remove icon class name
	 */
	#preview;

	/**
	 * Comparison results settings object
	 *
	 * @var Object
	 * @property	String	closeAttribute			The element selector for closing the comparison results section
	 * @property	Element	container				The comparison results section container element
	 * @property	Object	response				The product comparison API response object (populates after API request is issued)
	 * @property	String	response.function		The product comparison API function, this value should match #api.function value
	 * @property	Object	response.data			The product comparison API response data object
	 * @property	Integer	response.data.count		The product comparison API response data results count
	 * @property	Array	response.data.products	The product comparison API response data results objects
	 * @property	String	response.data.template	The product comparison API response data results template
	 * @property	Boolean	response.success		The product comparison API response success flag
	 * @property	Boolean	showCache				Flag to determine if the cached comparison results should display
	 * @property	String	trigger					The product comparison API request trigger element
	 */
	#results;

	/**
	 * Stickyfill object
	 *
	 * @var	Stickyfill
	 */
	#stickyfill;

	/**
	 * Parameter Validator object
	 *
	 * @var	@mvps-genesis/utilities	ParameterValidator
	 */
	#validator;

	/**
	 * Create a new ProductCompare object
	 *
	 * @param	String		options.compareElements				Overwrite the selector to add a product to the comparison list. Defaults to '.js-compare-select-container'.
	 * @param	String		options.cookieName					Overwrite the session cookie name used for persisting the comparison list. Defaults to 'mm5-prod-cmp-list'.
	 * @param	String		options.function					The product comparison JSON API function. Defaults to 'prod-cmp'.
	 * @param	Number		options.limit						Overwrite the maximum limit of selections. Defaults to 4.
	 * @param	Element		options.previewClearAttribute		Overwrite the comparison preview clear all attribute. Defaults to 'data-compare-preview-clear'.
	 * @param	Element		options.previewContainer			Overwrite the comparison preview container element. Defaults to '#js-compare-preview-container'.
	 * @param	Element		options.previewItemsContainer		Overwrite the comparison preview items container element. Defaults to '#js-compare-preview-items'.
	 * @param	string		options.previewItemsSelector		Overwrite the comparison preview item selector. Defaults to '.x-compare--preview-item'.
	 * @param	String		options.productIdAttribute			Overwrite the product identifier data attribute. This attribute must be present on each element in the options.compareElements list. Defaults to 'data-compare-product-id'.
	 * @param	String		options.productImageAttribute		Overwrite the product image path data attribute. Defaults to 'data-compare-product-image'.
	 * @param	String		options.productNameAttribute		Overwrite the product name data attribute. Defaults to 'data-compare-product-name'.
	 * @param	String		options.resultsCloseAttribute		Overwrite the attribute name to trigger closing the results modal. Defaults to 'data-compare-results-close'.
	 * @param	Element		options.resultsContainer			Overwrite the compare results container element. Defaults to '#js-compare-results-container'.
	 * @param	Element		options.resultsTrigger				Overwrite the compare results trigger element. When clicked, this element will display the compare results container with the comparison list items. Defaults to '#js-compare-results-trigger'.
	 * @param	String		options.url							The product comparison JSON API URL. Required value.
	 */

	// eslint-disable-next-line max-lines-per-function
	constructor (options = {}) {
		// Create param validator
		this.#validator = new ParameterValidator(this);

		// Provided options
		this.#options = this.#validator.isObject('options', options);

		// API object
		this.#api = {};
		this.#api.function = this.#validator.isString('options.function', options.function || 'prod-cmp');
		this.#api.url = this.#validator.isString('options.url', options.url);

		// Compare object
		this.#compare = {};
		this.#compare.elementsSelector = this.#validator.isString('options.compareElements', this.#options.compareElements || '.js-compare-select-container');
		this.#compare.elements = this.#validator.isElementList('compareElementsList', this.#compare.elementsSelector);
		this.#compare.list = [];
		this.#compare.limit = this.#validator.isInt('options.limit', this.#options.limit || 4);
		this.#compare.product = {};
		this.#compare.product.idAttribute = this.#validator.isString('options.productIdAttribute', this.#options.productIdAttribute || 'data-compare-product-id');
		this.#compare.product.imageAttribute = this.#validator.isString('options.productImageAttribute', this.#options.productImageAttribute || 'data-compare-product-image');
		this.#compare.product.nameAttribute = this.#validator.isString('options.productNameAttribute', this.#options.productNameAttribute || 'data-compare-product-name');

		// Cookie name
		this.#cookieName = this.#validator.isString('options.cookieName', this.#options.cookieName || 'mm5-prod-cmp-list');

		// Preview object
		this.#preview = {};
		this.#preview.activeClass = 'x-compare--preview-active';
		this.#preview.clearAttribute = this.#validator.isString('options.previewClearAttribute', this.#options.previewClearAttribute || 'data-compare-preview-clear');
		this.#preview.container = this.#validator.isElement('options.previewContainer', this.#options.previewContainer || '#js-compare-preview-container');
		this.#preview.itemsContainer = this.#validator.isElement('options.previewItemsContainer', this.#options.previewItemsContainer || '#js-compare-preview-items');
		this.#preview.itemsSelector = this.#validator.isString('options.previewItemsSelector', this.#options.previewItemsSelector || '.x-compare--preview-item');
		this.#preview.items = this.#validator.isElementList('previewItems', this.#preview.itemsSelector);
		this.#preview.product = {};
		this.#preview.product.containerClass = 'x-compare--preview-product';
		this.#preview.product.removeClass = 'x-compare--product-remove';
		this.#preview.product.removeIconClass = 'u-icon-minus-circle';

		// Results object
		this.#results = {};
		this.#results.closeAttribute = this.#validator.isString('options.resultsCloseAttribute', this.#options.resultsCloseAttribute || 'data-compare-results-close');
		this.#results.container = this.#validator.isElement('options.resultsContainer', this.#options.resultsContainer || '#js-compare-results-container');
		this.#results.data = {};
		this.#results.showCache = false;
		this.#results.trigger = this.#validator.isElement('options.resultsTrigger', this.#options.resultsTrigger || '#js-compare-results-trigger', {container: this.#preview.container});
		this.#results.stickyClass = 'is-sticky';

		// eslint-disable-next-line
		this.#stickyfill = Stickyfill();

		// Set the is mobile flag variable
		this.setMediaQueryList();

		// Bind base event listeners
		this.bindEvents();

		// Set sticky fill on the preview container
		const compareContainer = document.querySelector('.x-compare--container');

		if (compareContainer) {
			this.#stickyfill.add(compareContainer);
		}

		// Store the fasten header element
		this.fastenHeader = document.getElementById('fasten_header');

		// Set the initial comparison list
		this.setCompareList();
	}

	/*
	 *--------------------------------------------------------------------------
	 * Bind Events
	 *--------------------------------------------------------------------------
	 *
	 * Class Event binding methods.
	 *
	 */

	bindEvents () {
		// Bind compare elements (product cards)
		this.bindCompareElementsEvents();

		// Bind the clear all comparisons event
		this.bindClearAllEvents();

		// Bind the results trigger event
		this.#results.trigger.addEventListener('click', this.onResultsClick.bind(this));
	}

	bindCompareElementsEvents () {
		for (const compareEl of this.#compare.elements) {
			const compareElTrigger = compareEl.querySelector('input');

			if (compareElTrigger) {
				compareElTrigger.addEventListener('change', this.onCompareElementChange.bind(this, compareEl));
			}
		}
	}

	bindClearAllEvents () {
		const previewClearElements = this.#preview.container.querySelectorAll('[' + this.#preview.clearAttribute + ']');

		for (const prevClearEl of previewClearElements) {
			prevClearEl.addEventListener('click', this.onClearAllElementsClick.bind(this));
		}
	}

	/*
	 *--------------------------------------------------------------------------
	 * Event Handlers
	 *--------------------------------------------------------------------------
	 *
	 * Class Event handler methods.
	 *
	 */

	onCompareElementChange (compareEl, event) {
		const targetElement = this.#validator.isElement('event.target', event.target);

		if (targetElement.checked) {
			targetElement.disabled = true;

			// Add the element to the comparison list
			this.addCompareElement(compareEl);

			targetElement.disabled = false;

			return true;
		}

		// Remove compare element if it is already in comparison list
		this.removeCompareElement(compareEl);

		return true;
	}

	onClearAllElementsClick (event) {
		event.preventDefault();

		const previewEls = this.#preview.itemsContainer.querySelectorAll('.' + this.#preview.product.containerClass);

		for (const previewEl of previewEls) {
			this.removeCompareElement(previewEl);
		}
	}

	onResultsClearAllElementsClick (event) {
		this.onClearAllElementsClick(event);

		this.closeResultsModal();
	}

	onResultsClick (event) {
		event.preventDefault();

		// Set the results container with the comparison list items
		this.setResults();
	}

	/*
	 *--------------------------------------------------------------------------
	 * Helpler Methods
	 *--------------------------------------------------------------------------
	 *
	 * Comparison helper methods.
	 *
	 */

	// eslint-disable-next-line
	addCompareElement (element) {
		// Do not add element to comparison list if the list is already full
		if (this.isFullCompareList()) {
			return false;
		}

		// Create compare preview element
		const cmpPrevEl = this.createComparePreviewElement(element);

		// Find first available item slot and add preview element
		const compareItem = Array.from(this.#preview.items).find(item => {
			return !item.classList.contains('is-active');
		});

		if (!compareItem) {
			throw new Error('Unable to add element, no available compare item slots');
		}

		compareItem.appendChild(cmpPrevEl);
		compareItem.classList.add('is-active');

		// Add active class to selections container
		this.#preview.container.classList.add(this.#preview.activeClass);

		// Add the product id to the comparison list
		const elementProdId = this.getProductId(element);

		this.#compare.list.push(elementProdId);

		// Handle the results trigger disabled state and text content
		this.setResultsTriggerState();
		this.setResultsTriggerText();

		for (const compareEl of this.#compare.elements) {
			const compareElProdId = this.getProductId(compareEl);

			if (compareElProdId === elementProdId) {
				// Set the input state (:checked) if element exists in product listing
				const elementInput = compareEl.querySelector('input');

				if (elementInput) {
					elementInput.checked = true;
				}
			} else if (this.isFullCompareList() && !this.#compare.list.includes(compareElProdId)) {
				// If the comparison list is full, hide the Compare selections
				compareEl.classList.add('u-hidden');
			}
		}

		// Update the results cache display control
		this.#results.showCache = false;

		// Update the cookie value
		this.setCookie();

		return true;
	}

	// eslint-disable-next-line
	removeCompareElement (element) {
		const prodId = this.getProductId(element);

		// Remove the related preview element
		const previewEl = this.#preview.itemsContainer.querySelector('[' + this.#compare.product.idAttribute + '="' + prodId + '"]');

		if (!previewEl) {
			throw new Error('Unable to remove element, matching preview element not found');
		}

		const previewItem = previewEl.closest(this.#preview.itemsSelector);

		if (!previewItem) {
			throw new Error('Unable to remove element, matching preview item element not found');
		}

		previewItem.removeChild(previewEl);
		previewItem.classList.remove('is-active');

		for (const compareEl of this.#compare.elements) {
			// Update the compare element input state (:checked)
			if (this.getProductId(compareEl) === prodId) {
				const elementInput = compareEl.querySelector('input');

				if (elementInput) {
					compareEl.querySelector('input').checked = false;
				}
			}

			// Show any hidden Compare selections
			compareEl.classList.remove('u-hidden');
		}

		// Remove the item from the comparison list
		this.#compare.list = this.#compare.list.filter(id => {
			return id !== prodId;
		});

		// Handle the results trigger disabled state and text content
		this.setResultsTriggerState();
		this.setResultsTriggerText();

		// Hide the preview container if the comparison list is empty
		if (this.#compare.list.length === 0) {
			this.#preview.container.classList.remove(this.#preview.activeClass);
		}

		// Update the results cache display control
		this.#results.showCache = false;

		// Update the cookie value
		this.setCookie();

		return true;
	}

	setResultsTriggerState () {
		this.#results.trigger.disabled = this.#compare.list.length <= 1;
	}

	getProductId (element) {
		const productId = element.getAttribute(this.#compare.product.idAttribute);

		if (!productId) {
			throw new Error('Product identifier attribute missing on Element');
		}

		return productId;
	}

	async getResults (productIds) {
		const response = await fetch(this.#api.url, {
			body: JSON.stringify({
				function: this.#api.function,
				prod_codes: productIds,
				prod_availability: productIds.map(id => {
					const el = document.querySelector(`[data-compare-product-id="${id}"][data-compare-product-availability]`);
					return el ? el.getAttribute('data-compare-product-availability') || '' : '';
				})
			}),
			headers: {
				'Content-Type': 'application/json'
			},
			method: 'POST'
		});

		if (!response.ok) {
			throw new Error('Network response was not successful');
		}

		const responseBody = await response.json();

		return responseBody;
	}

	isFullCompareList () {
		return this.#compare.list.length >= this.#compare.limit;
	}

	async setCompareList () {
		// Get the session cookie value
		const cookieData = this.getCookieValue();

		// If there are active session comparisons, update the UI accordingly
		if (cookieData !== '') {
			const activeIds = cookieData.split(',');

			// Hide add action for Elements already in the comparison list
			if (activeIds && activeIds.length) {
				this.#results.response = await this.getResults(activeIds);

				if (this.#results.response.data && this.#results.response.data.products) {
					for (const product of this.#results.response.data.products) {
						const productElement = document.createElement('div');

						productElement.setAttribute(this.#compare.product.idAttribute, product.code);
						productElement.setAttribute(this.#compare.product.nameAttribute, product.name);
						productElement.setAttribute(this.#compare.product.imageAttribute, product.src);

						this.addCompareElement(productElement);
					}
				}
			}
		}

		// Uncheck any compare elements that are not in the compare list
		const selectedCompareInputs = document.querySelectorAll(this.#compare.elementsSelector + ' input:checked');

		if (selectedCompareInputs.length) {
			for (const selectedCompareInput of selectedCompareInputs) {
				const selectedCompareEl = selectedCompareInput.closest(this.#compare.elementsSelector);

				if (selectedCompareEl && !this.#compare.list.includes(this.getProductId(selectedCompareEl))) {
					selectedCompareInput.checked = false;
				}
			}
		}

		// Clear results container on init to prevent stale modal state
		this.#results.container.innerHTML = '';
		this.#results.container.setAttribute('aria-hidden', 'true');
	}

	setResultsTriggerText () {
		let textContent = this.#results.trigger.getAttribute('data-compare-trigger-text') || 'Compare';

		if (this.#compare.list.length) {
			textContent += ' (' + this.#compare.list.length + (this.#mql.matches ? '/' + this.#compare.limit : '') + ')';
		}

		this.#results.trigger.textContent = textContent;
	}

	setResultsTitleText () {
		if (!this.#results.titleElement) {
			this.#results.titleElement = this.#results.container.querySelector('#x-compare--results-title');
		}

		if (this.#results.titleElement) {
			let textContent = this.#results.titleElement.getAttribute('data-compare-results-title-text') || 'Compare Products';

			if (this.#mql.matches) {
				textContent = textContent.replace(/ Products/i, '');
			}

			if (this.#compare.list.length) {
				textContent += ' (' + this.#compare.list.length + ')';
			}

			this.#results.titleElement.textContent = textContent;
		}
	}

	getCookieValue () {
		const value = '; ' + document.cookie;
		const parts = value.split('; ' + this.#cookieName + '=');

		if (parts.length === 2) {
			return parts.pop()
				.split(';')
				.shift()
				.trim();
		}

		return '';
	}

	setCookie () {
		document.cookie = this.#cookieName + '=' + this.#compare.list.join(',') + '; expires=0; path=/';

		return true;
	}

	setMediaQueryList () {
		this.#mql = window.matchMedia('(max-width: 767px)');

		this.#mql.addListener(event => {
			this.#mql = event;

			this.setResultsTriggerText();
		});
	}

	/*
	 *--------------------------------------------------------------------------
	 * Comparison Preview
	 *--------------------------------------------------------------------------
	 *
	 * Comparison preview methods.
	 *
	 */

	createComparePreviewElement (element) {
		// Create the compare preview element
		const previewEl = document.createElement('div');

		previewEl.className = this.#preview.product.containerClass;
		previewEl.setAttribute(this.#compare.product.idAttribute, element.getAttribute(this.#compare.product.idAttribute));

		// Create the preview image element
		const previewImage = document.createElement('img');

		previewImage.setAttribute('src', element.getAttribute(this.#compare.product.imageAttribute));
		previewImage.setAttribute('alt', 'Compare ' + element.getAttribute(this.#compare.product.nameAttribute));
		previewEl.appendChild(previewImage);

		// Create the remove compare control element
		const removeCtrl = document.createElement('button');

		removeCtrl.setAttribute('type', 'button');
		removeCtrl.setAttribute('aria-label', 'Remove product from compare list');
		removeCtrl.classList.add(this.#preview.product.removeClass, 'g-button--no-styling');
		removeCtrl.innerHTML = '<span class="x-compare--remove-icon ' + this.#preview.product.removeIconClass + '"></span>';
		removeCtrl.addEventListener('click', () => {
			this.removeCompareElement(element);
		});

		previewEl.appendChild(removeCtrl);

		return previewEl;
	}

	/*
	 *--------------------------------------------------------------------------
	 * Comparison Results
	 *--------------------------------------------------------------------------
	 *
	 * Comparison results methods.
	 *
	 */

	async setResults () {
		this.#results.trigger.disabled = true;
		this.#results.trigger.textContent = 'Loading...';

		if (this.#results.showCache !== true) {
			// Get the product comparison results
			this.#results.response = await this.getResults(this.#compare.list);

			// Update results template with the new comparison data
			this.updateResultsTemplate();
		}

		// Show the results template
		this.showResultsTemplate();

		// Enable the trigger element
		this.#results.trigger.disabled = false;

		this.setResultsTriggerText();

		return true;
	}

	closeResultsModal () {
		if (this.#results.container.contains(document.activeElement)) {
			this.#results.trigger.focus();
		}

		MicroModal.close(this.#results.container.id);
	}

	showResultsTemplate () {
		MicroModal.show(this.#results.container.id, {
			awaitCloseAnimation: false,
			awaitOpenAnimation: false,
			closeTrigger: this.#results.closeAttribute,
			disableFocus: true,
			disableScroll: true,
			onClose: () => {
				if (this.fastenHeader) {
					this.fastenHeader.classList.remove('u-hidden');
				}
				document.querySelector('.g-site-header')?.classList.remove('u-hidden');
				document.querySelector('.g-site-header__top-navigation')?.classList.remove('u-hidden');
			},
			onShow: () => {
				if (this.fastenHeader) {
					this.fastenHeader.classList.add('u-hidden');
				}
				document.querySelector('.g-site-header')?.classList.add('u-hidden');
				document.querySelector('.g-site-header__top-navigation')?.classList.add('u-hidden');
			}
		});

		return true;
	}

	// eslint-disable-next-line
	updateResultsTemplate () {
		// Update the results cache display control
		if (this.#results.showCache === false) {
			this.#results.showCache = true;
		}

		// Set the results template
		this.#results.container.innerHTML = this.#results.response.data.template;

		// Set the results header title text
		this.setResultsTitleText();

		// Set the results header clear all events
		const resultsClearElements = this.#results.container.querySelectorAll('[' + this.#preview.clearAttribute + ']');

		for (const resClearEl of resultsClearElements) {
			resClearEl.addEventListener('click', this.onResultsClearAllElementsClick.bind(this));
		}

		// Set the results header close
		const resultsClose = this.#results.container.querySelector('[data-compare-results-content-close]');

		if (resultsClose) {
			resultsClose.addEventListener('click', event => {
				event.preventDefault();

				this.closeResultsModal();
			});
		}

		// Set the results individual close events
		const resultsRemoveElements = this.#results.container.querySelectorAll('.x-compare--product-remove');

		for (const resultsRemoveElement of resultsRemoveElements) {
			const resultsProductHeader = resultsRemoveElement.closest('.x-compare--results-product');

			if (resultsProductHeader) {
				const prodId = this.getProductId(resultsProductHeader);
				const resultsProductCells = this.#results.container.querySelectorAll('[' + this.#compare.product.idAttribute + '="' + prodId + '"]');

				resultsRemoveElement.addEventListener('click', () => {
					this.removeCompareElement(resultsProductHeader);

					for (const resultsProductCell of resultsProductCells) {
						resultsProductCell.parentElement.removeChild(resultsProductCell);
					}

					if (this.#compare.list.length < 1) {
						this.closeResultsModal();
					}
				});
			}
		}

		// Set the sticky fill on the table headers
		const tableHeader = this.#results.container.querySelector('.x-compare--results-table-header');

		if (tableHeader) {
			this.#stickyfill.add(tableHeader);
		}

		this.bindResultsTableEvents();

		return true;
	}

	bindResultsTableEvents () {
		// Bind results table scroll events
		this.resultsTableOnScroll();
	}

	/* eslint-disable max-lines-per-function */
	resultsTableOnScroll () {
		const table = this.#results.container.querySelector('.x-compare--results-table');

		if (!table) {
			return false;
		}

		const tableHeader = table.querySelector('.x-compare--results-table-header');

		if (!tableHeader) {
			return false;
		}

		// Delay in seconds, this should match timing of the this.#results.stickyClass transition
		const timerDelay = 0.25;
		const stickyLeftClassname = `${this.#results.stickyClass}-x`;
		let isStickyTop = false;
		let isStickyLeft = false;
		let classTimer = null;
		let tableScrollTop = 0;

		table.addEventListener('scroll', () => {
			// Table sticky vertical scroll
			if (table.scrollTop > 0 && !isStickyTop) {
				window.requestAnimationFrame(() => {
					tableScrollTop = table.scrollTop;
					tableHeader.classList.add(this.#results.stickyClass);
					classTimer = new Date();
					isStickyTop = true;
				});
			} else if (table.scrollTop === 0 && isStickyTop) {
				const now = new Date();

				if (classTimer !== null && (now.getTime() - classTimer.getTime()) / 1000 < timerDelay) {
					table.scrollTop = tableScrollTop;
				} else {
					window.requestAnimationFrame(() => {
						tableHeader.classList.remove(this.#results.stickyClass);
						isStickyTop = false;
					});
				}
			}

			// Table sticky horizontal scroll
			if (table.scrollLeft > 0 && !isStickyLeft) {
				window.requestAnimationFrame(() => {
					table.classList.add(stickyLeftClassname);
					isStickyLeft = true;
				});
			} else if (table.scrollLeft === 0 && isStickyLeft) {
				window.requestAnimationFrame(() => {
					table.classList.remove(stickyLeftClassname);
					isStickyLeft = false;
				});
			}
		});

		return true;
	}
	/* eslint-enable max-lines-per-function */
}